import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Calendar, Mail, Phone, Building2, User as UserIcon, TrendingUp, Clock, Search, ShieldAlert, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { NostrEvent } from '@nostrify/nostrify';
import { Skeleton } from '@/components/ui/skeleton';
import { nip19 } from 'nostr-tools';
import { Link } from 'react-router-dom';
import { isAdminPubkey } from '@/lib/adminConfig';

interface ProfileData {
  email?: string;
  phone?: string;
  userType?: 'individual' | 'business';
  authProvider?: string;
  registeredAt?: string;
}

const Dashboard = () => {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [searchQuery, setSearchQuery] = useState('');

  useSeoMeta({
    title: 'Dashboard - Bitcoin Life Panamá',
    description: 'User registration dashboard',
  });

  // Check if current user is admin
  const isAdmin = isAdminPubkey(user?.pubkey);

  // If not admin, show access denied
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-950 flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-orange-600" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Acceso Restringido</h2>
              <p className="text-muted-foreground">
                Debes iniciar sesión para acceder al dashboard.
              </p>
            </div>
            <Link to="/">
              <Button className="w-full">
                Volver al Inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-950 flex items-center justify-center mx-auto">
              <ShieldAlert className="w-8 h-8 text-red-600" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Acceso Denegado</h2>
              <p className="text-muted-foreground">
                No tienes permisos para acceder a esta página. Solo los administradores pueden ver el dashboard.
              </p>
            </div>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Volver al Inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fetch all user profiles (kind 0 events)
  const { data: profiles, isLoading: profilesLoading } = useQuery({
    queryKey: ['user-profiles'],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query(
        [{ kinds: [0], limit: 100 }],
        { signal }
      );
      return events;
    },
  });

  // Fetch application-specific data (kind 30078 - custom profile data)
  const { data: customProfiles, isLoading: customLoading } = useQuery({
    queryKey: ['custom-profiles'],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query(
        [{
          kinds: [30078],
          '#d': ['bitcoin-life-panama-profile'],
          limit: 100
        }],
        { signal }
      );
      return events;
    },
  });

  // Parse custom profile data
  const parseCustomProfile = (event: NostrEvent): ProfileData | null => {
    try {
      const content = JSON.parse(event.content);
      const email = event.tags.find(t => t[0] === 'email')?.[1];
      const phone = event.tags.find(t => t[0] === 'phone')?.[1];
      const userType = event.tags.find(t => t[0] === 'user_type')?.[1] as 'individual' | 'business';
      const authProvider = event.tags.find(t => t[0] === 'auth_provider')?.[1];
      const registeredAt = event.tags.find(t => t[0] === 'registered_at')?.[1];

      return {
        email,
        phone,
        userType,
        authProvider,
        registeredAt,
        ...content
      };
    } catch {
      return null;
    }
  };

  // Combine profiles with custom data
  const combinedUsers = profiles?.map(profile => {
    try {
      const metadata = JSON.parse(profile.content);
      const customData = customProfiles?.find(cp => cp.pubkey === profile.pubkey);
      const parsedCustomData = customData ? parseCustomProfile(customData) : null;

      return {
        pubkey: profile.pubkey,
        npub: nip19.npubEncode(profile.pubkey),
        name: metadata.name || 'Anonymous',
        displayName: metadata.display_name,
        about: metadata.about,
        picture: metadata.picture,
        nip05: metadata.nip05,
        createdAt: profile.created_at,
        customData: parsedCustomData,
      };
    } catch {
      return null;
    }
  }).filter(Boolean) || [];

  // Filter users based on search
  const filteredUsers = combinedUsers.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user?.name?.toLowerCase().includes(query) ||
      user?.npub?.toLowerCase().includes(query) ||
      user?.customData?.email?.toLowerCase().includes(query) ||
      user?.customData?.phone?.includes(query)
    );
  });

  // Statistics
  const totalUsers = combinedUsers.length;
  const businessUsers = combinedUsers.filter(u => u?.customData?.userType === 'business').length;
  const individualUsers = combinedUsers.filter(u => u?.customData?.userType === 'individual').length;
  const usersWithEmail = combinedUsers.filter(u => u?.customData?.email).length;

  const isLoading = profilesLoading || customLoading;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 text-gray-900 dark:text-white">
              Dashboard de Usuarios
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Registros y actividad de la plataforma Bitcoin Life Panamá
            </p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Usuarios</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <>
                    <div className="text-2xl font-bold">{totalUsers}</div>
                    <p className="text-xs text-muted-foreground">
                      Perfiles registrados en Nostr
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Negocios</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <>
                    <div className="text-2xl font-bold">{businessUsers}</div>
                    <p className="text-xs text-muted-foreground">
                      Comercios registrados
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Individuales</CardTitle>
                <UserIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <>
                    <div className="text-2xl font-bold">{individualUsers}</div>
                    <p className="text-xs text-muted-foreground">
                      Usuarios individuales
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Con Email</CardTitle>
                <Mail className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <>
                    <div className="text-2xl font-bold">{usersWithEmail}</div>
                    <p className="text-xs text-muted-foreground">
                      Datos de contacto
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Search and Filters */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Buscar Usuarios</CardTitle>
              <CardDescription>
                Busca por nombre, npub, email o teléfono
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar usuarios..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Users List */}
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">Todos ({filteredUsers.length})</TabsTrigger>
              <TabsTrigger value="business">Negocios ({filteredUsers.filter(u => u?.customData?.userType === 'business').length})</TabsTrigger>
              <TabsTrigger value="individual">Individuales ({filteredUsers.filter(u => u?.customData?.userType === 'individual').length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="space-y-4 mt-6">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <Card key={i}>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                          <Skeleton className="w-12 h-12 rounded-full" />
                          <div className="space-y-2 flex-1">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filteredUsers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {searchQuery ? 'No se encontraron usuarios' : 'No hay usuarios registrados aún'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filteredUsers.map((user) => (
                  <Card key={user?.pubkey} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={user?.picture} alt={user?.name} />
                          <AvatarFallback>{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{user?.name}</h3>
                              {user?.customData?.userType && (
                                <Badge variant={user.customData.userType === 'business' ? 'default' : 'secondary'}>
                                  {user.customData.userType === 'business' ? (
                                    <>
                                      <Building2 className="w-3 h-3 mr-1" />
                                      Negocio
                                    </>
                                  ) : (
                                    <>
                                      <UserIcon className="w-3 h-3 mr-1" />
                                      Individual
                                    </>
                                  )}
                                </Badge>
                              )}
                            </div>
                            {user?.about && (
                              <p className="text-sm text-muted-foreground line-clamp-2">{user.about}</p>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            {user?.customData?.email && (
                              <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">{user.customData.email}</span>
                              </div>
                            )}
                            {user?.customData?.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">{user.customData.phone}</span>
                              </div>
                            )}
                            {user?.createdAt && (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">
                                  {new Date(user.createdAt * 1000).toLocaleDateString('es-PA')}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground text-xs font-mono truncate">
                                {user?.npub.substring(0, 16)}...
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="business" className="space-y-4 mt-6">
              {filteredUsers.filter(u => u?.customData?.userType === 'business').map((user) => (
                <Card key={user?.pubkey} className="hover:shadow-lg transition-shadow border-orange-200 dark:border-orange-800">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback>{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">{user?.name}</h3>
                            <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                              <Building2 className="w-3 h-3 mr-1" />
                              Comercio
                            </Badge>
                          </div>
                          {user?.about && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{user.about}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          {user?.customData?.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{user.customData.email}</span>
                            </div>
                          )}
                          {user?.customData?.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{user.customData.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="individual" className="space-y-4 mt-6">
              {filteredUsers.filter(u => u?.customData?.userType === 'individual').map((user) => (
                <Card key={user?.pubkey} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback>{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">{user?.name}</h3>
                            <Badge variant="secondary">
                              <UserIcon className="w-3 h-3 mr-1" />
                              Individual
                            </Badge>
                          </div>
                          {user?.about && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{user.about}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          {user?.customData?.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{user.customData.email}</span>
                            </div>
                          )}
                          {user?.customData?.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{user.customData.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
